/**
 * Generate (spec 027), with the provider mark. Its tooltip says what will
 * run before anything does; with fewer units left than the request needs it
 * is disabled and says so. Renders nothing when generation is off or not
 * set up. `secondary` is the quieter "Generate new" of a filled slot (D-21).
 */
import React from 'react';
import type { CharacterFraming, PictureSlot } from '../../../../Shared/src/@types';
import { isSlotFilled, isSlotNeeded } from '../../../../Shared/src/pictures';
import { useImageGenStore, type PendingGenerate } from '../../stores/imageGenStore';
import { briefTextFor, generateTooltip, IMAGE_TEXT } from './imageGenText';
import { usePictureOwner } from '../pictures/pictureOwner';
import ProviderMark from './ProviderMark';
import styles from './ImageGen.module.scss';

export interface GenerateButtonProps {
  slotIds: string[];
  label?: string;
  request?: PendingGenerate['request'];
  onStart?: (result: 'started' | 'gate' | 'failed') => void;
  testId?: string;
  /** The secondary look ("Generate new" on a filled slot, spec 027 D-21). */
  secondary?: boolean;
  /** Why it cannot run yet (no brief, D-23): disabled, with this as its tooltip. */
  blockedReason?: string;
  /** Runs first (the panel saves an edited brief); `false` starts nothing. */
  beforeStart?: () => Promise<boolean>;
}

export const GenerateButton: React.FC<GenerateButtonProps> = ({
  slotIds, label, request, onStart, testId = 'image-generate', secondary = false, blockedReason, beforeStart,
}) => {
  const settings = useImageGenStore((s) => s.settings);
  const generate = useImageGenStore((s) => s.generate);
  if (!settings || settings.mode === 'off' || !settings.available || slotIds.length === 0) return null;
  const limit = generateTooltip(settings, slotIds.length, request?.count);
  const disabled = limit.disabled || !!blockedReason;
  const tooltip = blockedReason ?? limit.lines.join('\n');
  return (
    <button
      type="button"
      className={secondary ? styles.generateNew : styles.generate}
      disabled={disabled}
      title={tooltip}
      aria-label={label ?? IMAGE_TEXT.generate}
      data-tooltip={tooltip}
      data-testid={testId}
      onClick={(e) => {
        e.stopPropagation();
        if (!beforeStart) {
          void generate(slotIds, request).then((r) => onStart?.(r));
          return;
        }
        void beforeStart().then((go) => (go ? generate(slotIds, request).then((r) => onStart?.(r)) : undefined));
      }}
    >
      <ProviderMark />
      <span>{label ?? IMAGE_TEXT.generate}</span>
    </button>
  );
};

/** The framing a slot's job is sent with (characters only, D-22). */
const framingFor = (slot: PictureSlot, framing?: CharacterFraming): CharacterFraming | undefined =>
  (slot.kind === 'character' ? framing ?? slot.framing : undefined);

/**
 * A slot's own Generate (D-21): the primary Generate on a needed slot, the
 * secondary "Generate new" (`replace: true`) on a filled one, nothing for a
 * slot without uses. Disabled while the brief is empty (D-23). `where` names
 * the test ids (`card-generate`, `card-generate-new`, `panel-generate`,
 * `panel-generate-new`). The panel passes the field's `brief`, the chosen
 * `framing` and `beforeStart` (which saves an edited brief).
 */
export const SlotGenerateButton: React.FC<{
  slot: PictureSlot;
  where: 'card' | 'panel';
  brief?: string;
  framing?: CharacterFraming;
  beforeStart?: () => Promise<boolean>;
}> = ({ slot, where, brief, framing, beforeStart }) => {
  const noun = usePictureOwner().noun;
  const text = (brief ?? slot.brief ?? '').trim();
  const f = framingFor(slot, framing);
  const base = {
    ...(brief !== undefined && text ? { briefs: { [slot.id]: text } } : {}),
    ...(f ? { framings: { [slot.id]: f } } : {}),
  };
  const blockedReason = text ? undefined : briefTextFor(noun).needsBrief;
  if (isSlotNeeded(slot)) {
    return <GenerateButton slotIds={[slot.id]} request={base} blockedReason={blockedReason} beforeStart={beforeStart} testId={`${where}-generate`} />;
  }
  if (slot.uses.length > 0 && isSlotFilled(slot)) {
    return (
      <GenerateButton
        slotIds={[slot.id]}
        label={IMAGE_TEXT.generateNew}
        request={{ ...base, replace: true }}
        secondary
        blockedReason={blockedReason}
        beforeStart={beforeStart}
        testId={`${where}-generate-new`}
      />
    );
  }
  return null;
};

export default GenerateButton;
